import { useEffect } from 'react';
import { siteConfig } from '../siteConfig';
import ogRoutes from '../data/ogRoutes.json';

const DEFAULT_IMAGE = `${siteConfig.url}/assets/images/og-image.png`;

// One generated image per route. Fall back to the default for anything
// without a generated hero image, such as the 404 page.
const ogImages = new Set(ogRoutes);

const ogImageFor = (path) => {
  if (!ogImages.has(path)) return DEFAULT_IMAGE;
  const slug = path === '/' ? 'home' : path.replace(/^\//, '').replace(/\//g, '-');
  return `${siteConfig.url}/assets/images/og/${slug}.png`;
};

const setMeta = (attribute, key, content) => {
  if (!content) return;
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const normalizePath = (path) => {
  if (!path || path === '/') return '/';
  return `/${path.replace(/^\/+|\/+$/g, '')}`;
};

export const usePageMeta = ({ title, description, path = '/', image, jsonLd, robots = 'index, follow' }) => {
  useEffect(() => {
    const canonical = `${siteConfig.url}${normalizePath(path)}`;
    const ogImage = image || ogImageFor(normalizePath(path));

    document.documentElement.lang = 'en-AU';
    document.title = title;

    setMeta('name', 'description', description);
    setMeta('name', 'robots', robots);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', siteConfig.name);
    setMeta('property', 'og:locale', 'en_AU');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', ogImage);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);
    setMeta('name', 'twitter:url', canonical);

    setLink('canonical', canonical);

    document.querySelectorAll('script[data-seo-jsonld="true"]').forEach((element) => element.remove());

    if (jsonLd) {
      const payload = Array.isArray(jsonLd)
        ? { '@context': 'https://schema.org', '@graph': jsonLd }
        : jsonLd;

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.seoJsonld = 'true';
      script.textContent = JSON.stringify(payload);
      document.head.appendChild(script);
    }

    document.body.dataset.seoReady = 'true';
    document.body.dataset.seoPath = normalizePath(path);
  }, [title, description, path, image, jsonLd, robots]);
};

export const organizationSchema = () => {
  const schema = {
    '@type': 'Organization',
    '@id': `${siteConfig.url}/#organization`,
    name: siteConfig.name,
    legalName: siteConfig.legalName,
    url: siteConfig.url,
    logo: `${siteConfig.url}/Icon-512.png`,
    description: 'Australia-based software studio providing websites, AI receptionists, website chatbots and custom software.',
    disambiguatingDescription: 'CloudNext is an Australia-based software studio in Hervey Bay, Queensland, providing websites, AI receptionists and custom software. It is not affiliated with other companies using the CloudNext name.',
    founder: {
      '@type': 'Person',
      name: siteConfig.legalName,
      jobTitle: 'Founder',
      sameAs: siteConfig.founderLinkedin,
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Hervey Bay',
      addressRegion: 'QLD',
      addressCountry: 'AU',
    },
    areaServed: {
      '@type': 'Country',
      name: 'Australia',
    },
    brand: {
      '@type': 'Brand',
      name: 'BulletChat',
      url: siteConfig.bulletchat,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: siteConfig.email,
      areaServed: 'AU',
      availableLanguage: 'English',
    },
    knowsAbout: [
      'AI receptionist',
      'voice AI',
      'website chatbot',
      'appointment booking',
      'lead capture',
      'custom software',
      'MVP development',
      'SaaS development',
    ],
    sameAs: [siteConfig.linkedin, siteConfig.founderLinkedin],
  };

  if (siteConfig.phone) schema.contactPoint.telephone = siteConfig.phone;
  if (siteConfig.abn) schema.taxID = siteConfig.abn;

  return schema;
};

export const serviceSchema = ({ name, serviceType, path, description, audience }) => ({
  '@type': 'Service',
  name,
  serviceType,
  description,
  provider: { '@id': `${siteConfig.url}/#organization` },
  url: `${siteConfig.url}${normalizePath(path)}`,
  areaServed: { '@type': 'Country', name: 'Australia' },
  audience: audience
    ? { '@type': 'BusinessAudience', name: audience }
    : { '@type': 'BusinessAudience', name: 'Australian trades and service businesses' },
});

export const faqSchema = (items) => ({
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
});

export const breadcrumbSchema = (items) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${siteConfig.url}${item.path}`,
  })),
});
